import { useState, useMemo } from 'react';
import { AlertTriangle, CheckCircle2, Info, FileJson, ArrowRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { InlineLoader } from '@/components/ui/inline-loader';
import {
  transformN8nToEwa,
  reportImportGaps,
  type N8nWorkflow,
  type TransformResult,
  type ImportGap,
} from '@/lib/n8n-import';

interface N8nImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  n8nWorkflow: N8nWorkflow;
  onImport: (result: TransformResult) => void;
}

export function N8nImportDialog({
  open,
  onOpenChange,
  n8nWorkflow,
  onImport,
}: N8nImportDialogProps) {
  const [importing, setImporting] = useState(false);

  // Transform on dialog open
  const transformResult = useMemo(() => {
    if (!n8nWorkflow) return null;
    try {
      return transformN8nToEwa(n8nWorkflow);
    } catch (error) {
      console.error('[n8n-import] Transform error:', error);
      return null;
    }
  }, [n8nWorkflow]);

  const handleImport = async () => {
    if (!transformResult) return;

    setImporting(true);
    try {
      // Report gaps to backend for roadmap tracking (fire and forget)
      if (transformResult.gaps.length > 0) {
        reportImportGaps(
          transformResult.sessionId,
          null, // workflow ID will be set after creation
          transformResult.gaps
        ).catch(() => {}); // Silently ignore errors
      }

      // Call parent handler with the transform result
      onImport(transformResult);
      onOpenChange(false);
    } catch (error) {
      console.error('[n8n-import] Import error:', error);
    } finally {
      setImporting(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  if (!transformResult) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Import n8n Workflow</DialogTitle>
            <DialogDescription>
              Unable to transform the workflow. Please check the JSON format.
            </DialogDescription>
          </DialogHeader>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Transform Error</AlertTitle>
            <AlertDescription>
              The pasted content could not be converted to EWA format.
              Make sure it's a valid n8n workflow export.
            </AlertDescription>
          </Alert>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const { stats, gaps, warnings, workflow } = transformResult;
  const unsupportedNodeGaps = gaps.filter(g => g.gapType === 'UnsupportedNode');
  const unsupportedParamGaps = gaps.filter(g => g.gapType === 'UnsupportedParameter');
  const hasGaps = gaps.length > 0;
  const hasWarnings = warnings.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileJson className="h-5 w-5" />
            Import n8n Workflow
          </DialogTitle>
          <DialogDescription>
            Convert and import this n8n workflow into EWA format
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4">
          <div className="space-y-4">
            {/* Conversion Stats */}
            <div className="rounded-lg border p-4 space-y-3">
              <h4 className="font-medium text-sm">Conversion Summary</h4>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="space-y-1">
                  <div className="text-2xl font-bold">{stats.totalNodes}</div>
                  <div className="text-xs text-muted-foreground">Total Nodes</div>
                </div>
                <div className="space-y-1">
                  <div className="text-2xl font-bold text-green-600">{stats.mappedNodes}</div>
                  <div className="text-xs text-muted-foreground">Fully Supported</div>
                </div>
                <div className="space-y-1">
                  <div className="text-2xl font-bold text-amber-600">{stats.placeholderNodes}</div>
                  <div className="text-xs text-muted-foreground">Placeholders</div>
                </div>
              </div>
              {stats.totalEdges > 0 && (
                <div className="text-xs text-muted-foreground text-center">
                  {stats.totalEdges} connection{stats.totalEdges !== 1 ? 's' : ''} will be preserved
                </div>
              )}
            </div>

            {/* Warnings */}
            {hasWarnings && (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>Important Notes</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc pl-4 mt-1 space-y-1">
                    {warnings.map((warning, idx) => (
                      <li key={idx} className="text-sm">{warning}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Unsupported Nodes */}
            {unsupportedNodeGaps.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Unsupported Nodes ({unsupportedNodeGaps.length})
                </h4>
                <p className="text-xs text-muted-foreground">
                  These nodes will be converted to no-op placeholders. You can manually configure alternatives.
                </p>
                <div className="rounded-lg border divide-y max-h-[150px] overflow-y-auto">
                  {unsupportedNodeGaps.map((gap, idx) => (
                    <div key={idx} className="px-3 py-2 flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{gap.sourceNodeName}</div>
                        <div className="text-xs text-muted-foreground truncate">{gap.sourceNodeType}</div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <Badge variant="secondary" className="flex-shrink-0">no-op</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Unsupported Parameters */}
            {unsupportedParamGaps.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <Info className="h-4 w-4 text-blue-500" />
                  Partially Supported Features ({unsupportedParamGaps.length})
                </h4>
                <p className="text-xs text-muted-foreground">
                  These parameters are not yet supported and will be skipped.
                </p>
                <div className="rounded-lg border divide-y max-h-[120px] overflow-y-auto">
                  {unsupportedParamGaps.slice(0, 10).map((gap, idx) => (
                    <div key={idx} className="px-3 py-2">
                      <div className="text-sm">
                        <span className="font-medium">{gap.sourceNodeName}</span>
                        <span className="text-muted-foreground"> → </span>
                        <code className="text-xs bg-muted px-1 py-0.5 rounded">{gap.parameterName}</code>
                      </div>
                      <div className="text-xs text-muted-foreground">{gap.description}</div>
                    </div>
                  ))}
                  {unsupportedParamGaps.length > 10 && (
                    <div className="px-3 py-2 text-xs text-muted-foreground text-center">
                      And {unsupportedParamGaps.length - 10} more...
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Success State */}
            {!hasGaps && !hasWarnings && (
              <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertTitle className="text-green-600">Full Compatibility</AlertTitle>
                <AlertDescription className="text-green-600">
                  All nodes in this workflow are fully supported!
                </AlertDescription>
              </Alert>
            )}

            <Separator />

            {/* Info about roadmap tracking */}
            {hasGaps && (
              <div className="text-xs text-muted-foreground">
                <Info className="h-3 w-3 inline mr-1" />
                Unsupported features are automatically reported to help us prioritize future development.
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={handleCancel} disabled={importing}>
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={importing}>
            {importing ? (
              <InlineLoader size="sm" text="Importing..." />
            ) : (
              <>
                Import Workflow
                {stats.placeholderNodes > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {stats.placeholderNodes} placeholder{stats.placeholderNodes !== 1 ? 's' : ''}
                  </Badge>
                )}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
